import { ReactNode, useEffect, useRef } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import "@xterm/xterm/css/xterm.css";
import Heading from "./Heading";

export interface LogEntry {
    timestamp?: string;
    content: string;
}

interface XTermLogProps {
    logs: LogEntry[];
    height?: string | number;
    linkify?: boolean;
    className?: string;
    title?: ReactNode;
    extra?: ReactNode;
}

const XTERM_THEME = {
    background: "#121212",
    foreground: "#ffffff",
    selectionBackground: "#262626",
    cursor: "#121212",
};

const DIM = "\x1b[38;2;107;114;128m"; // #6B7280 muted gray
const RESET = "\x1b[0m";

function formatEntry(entry: LogEntry): string {
    if (entry.timestamp) {
        return `${DIM}${entry.timestamp}${RESET} ${entry.content}`;
    }
    return entry.content;
}

const XTermLog = ({ logs, height, linkify, className, title, extra }: XTermLogProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const terminalRef = useRef<Terminal | null>(null);
    const fitAddonRef = useRef<FitAddon | null>(null);
    const prevLogsRef = useRef<LogEntry[]>([]);

    useEffect(() => {
        if (!containerRef.current) return;

        const terminal = new Terminal({
            theme: XTERM_THEME,
            fontSize: 12,
            fontFamily: "monospace",
            scrollback: 10000,
            disableStdin: true,
            convertEol: true,
            cursorStyle: "bar",
            cursorBlink: false,
            cursorInactiveStyle: "none",
        });

        const fitAddon = new FitAddon();
        terminal.loadAddon(fitAddon);

        if (linkify) {
            terminal.loadAddon(new WebLinksAddon());
        }

        terminal.open(containerRef.current);
        fitAddon.fit();

        terminalRef.current = terminal;
        fitAddonRef.current = fitAddon;
        prevLogsRef.current = [];

        const resizeObserver = new ResizeObserver(() => {
            fitAddon.fit();
        });
        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
            terminal.dispose();
            terminalRef.current = null;
            fitAddonRef.current = null;
            prevLogsRef.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkify]);

    useEffect(() => {
        const terminal = terminalRef.current;
        if (!terminal) return;

        const prev = prevLogsRef.current;
        const prevLength = prev.length;

        // Detect if this is a full replacement (not just appending new lines)
        const isAppend = logs.length >= prevLength &&
            prevLength > 0 &&
            prev[0].content === logs[0].content;

        if (isAppend) {
            for (let i = prevLength; i < logs.length; i++) {
                terminal.writeln(formatEntry(logs[i]));
            }
        } else {
            terminal.reset();
            for (const entry of logs) {
                terminal.writeln(formatEntry(entry));
            }
        }

        prevLogsRef.current = logs;
    }, [logs]);

    return (
        <div className={className} style={{ height }}>
            {title && (
                <div style={{ marginTop: -30 }}>
                    <Heading
                        level={5}
                        title={
                            <div className="flex-justify-space-between flex-align-center">
                                <div style={{ color: "white" }}>{title}</div>
                                {extra}
                            </div>
                        }
                    />
                </div>
            )}
            <div ref={containerRef} style={{ height: "100%", width: "100%" }} />
        </div>
    );
};

export default XTermLog;
